import { useEffect, useState } from "react";

import * as Sentry from "@sentry/browser";
import {
  Button,
  CopyButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  Heading,
  Switch,
} from "@stacklet/ui";
import { graphql, useFragment, useMutation } from "react-relay";

import { useAlertContext } from "app/contexts";
import { SetAwsAccessRolesMutation } from "app/mutations/SetAwsAccessRoles";

import { type AccessModelSettings_awsAccessRoles$key } from "./__generated__/AccessModelSettings_awsAccessRoles.graphql";
import { GatewayRolesForm, setAwsAccessRolesUpdater } from "./GatewayRolesForm";

import type { SetAwsAccessRolesMutation as SetAwsAccessRolesMutationType } from "app/mutations/__generated__/SetAwsAccessRolesMutation.graphql";

function getExecutionRole(samplePrimaryTrust: string) {
  let executionRole = "";
  try {
    const awsPrincipal =
      JSON.parse(samplePrimaryTrust).Statement[0]?.Principal.AWS;
    executionRole =
      awsPrincipal?.find((arn: string) => arn.includes("execution")) || "";
  } catch (e) {
    Sentry.captureException(e);
  }

  return executionRole;
}

interface Props {
  queryRef: AccessModelSettings_awsAccessRoles$key;
}

export function AccessModelSettings({ queryRef }: Props) {
  const { alertDispatch } = useAlertContext();

  const [updateAwsAccessRoles, isInFlight] =
    useMutation<SetAwsAccessRolesMutationType>(SetAwsAccessRolesMutation);

  const data = useFragment<AccessModelSettings_awsAccessRoles$key>(
    graphql`
      fragment AccessModelSettings_awsAccessRoles on Query
      @refetchable(queryName: "AccessModelSettingsRefetchQuery") {
        ...GatewayRolesForm_awsAccessRoles
        awsAccessRoles {
          externalID
          primaryRoleARN
          samplePrimaryTrust
        }
      }
    `,
    queryRef,
  );

  const roleKeys = [
    "primaryRoleARN",
    "readOnlyRoleARN",
    "accountDiscoveryRoleARN",
    "remediationPolicyRoleARN",
  ];
  const userHasGatewayRoles = roleKeys.some(
    (roleKey) =>
      !!data.awsAccessRoles[roleKey as keyof typeof data.awsAccessRoles],
  );
  const [wasGatewayRolesToggled, setWasGatewayRolesToggled] = useState(false);
  const [isGatewayRolesToggled, setIsGatewayRolesToggled] =
    useState(userHasGatewayRoles);

  useEffect(() => {
    setIsGatewayRolesToggled(userHasGatewayRoles);
  }, [userHasGatewayRoles]);

  function disableGatewayRoles() {
    updateAwsAccessRoles({
      variables: {
        input: {
          primary: null,
          readOnly: null,
          accountDiscovery: null,
          remediationPolicy: null,
        },
      },
      onCompleted(_, errors) {
        if (errors) {
          Sentry.captureException(errors);
        } else {
          alertDispatch({
            message: "Gateway roles disabled successfully",
            severity: "success",
          });
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
      updater: setAwsAccessRolesUpdater,
    });
  }

  const { primaryRoleARN, externalID, samplePrimaryTrust } =
    data.awsAccessRoles;

  const executionRoleARN = getExecutionRole(samplePrimaryTrust);
  return (
    <>
      <Dialog
        aria-label="disable-gateway-roles-modal"
        isOpen={wasGatewayRolesToggled}
        onOpenChange={(isOpen) => {
          if (!isOpen) setWasGatewayRolesToggled(false);
        }}
        overlayClassName="z-[1300]"
        size="lg"
        isDismissable
      >
        <DialogHeader hideCloseButton>
          Disable Gateway Roles (Chain Assume Role)
        </DialogHeader>
        <DialogContent>
          <p className="mb-md text-body-large">
            Stacklet will use the default security context on each account
            rather than the gateway role.
          </p>
          <p className="text-body-large">
            Ensure that the roles on each account have a valid trust
            relationship to the Stacklet execution role to ensure that AssetDB,
            Policy Execution, and Account Discovery continues uninterrupted.
          </p>
        </DialogContent>
        <DialogFooter className="justify-between">
          <Button
            onPress={() => setWasGatewayRolesToggled(false)}
            variant="tertiary"
          >
            Cancel
          </Button>
          <Button
            isDisabled={isInFlight}
            isPending={isInFlight}
            onPress={() => {
              setWasGatewayRolesToggled(false);
              disableGatewayRoles();
            }}
            pendingText="Disabling..."
            variant="primary"
          >
            Disable Gateway Roles
          </Button>
        </DialogFooter>
      </Dialog>
      <div className="flex flex-col gap-xl">
        <Switch
          isSelected={isGatewayRolesToggled}
          onChange={() => {
            if (userHasGatewayRoles) {
              setWasGatewayRolesToggled(true);
            } else {
              setIsGatewayRolesToggled((prev) => !prev);
            }
          }}
          position="trailing"
        >
          Enable Gateway Roles (Chain Assume Role)
        </Switch>
        <div className="flex flex-col gap-lg">
          <p className="text-body-medium text-text-secondary">
            When <b>Gateway Roles (chain assume roles) are enabled</b>, Stacklet
            will first assume into the Gateway Roles you&apos;ve provided, and
            then assume into each accounts&apos; security context role. This
            allows you to disable Stacklet&apos;s policy execution access from a
            single role, by simply removing the trust relationship for
            Stacklet&apos;s execution role.
          </p>
          <ul className="list-disc space-y-sm pl-xl text-body-medium text-text-secondary marker:text-text-secondary">
            <li>
              To enable chain assume role, create a “Primary Role” that can
              trust the Stacklet execution role by using this Trust
              Relationship:{" "}
              <CopyButton
                aria-label="Copy Trust Relationship button in description list"
                className="inline-flex"
                copyLabel="Trust Relationship"
                value={samplePrimaryTrust || ""}
                variant="stacklet-orange"
              >
                Copy Trust Relationship
              </CopyButton>
            </li>
            <li>
              Next, ensure that the Primary Role has the ability to assume into
              the roles available in each account&apos;s security context.
            </li>
            <li>Finally, add and save the Primary Role ARN.</li>
          </ul>
        </div>
        <div className="flex flex-col gap-lg">
          <div className="flex flex-col gap-sm">
            <Heading level={3}>Stacklet execution role</Heading>
            <div className="w-fit rounded-1 border border-outline-decorative px-sm font-semibold text-text-brand">
              {executionRoleARN}
            </div>
            <div className="flex items-center gap-xl">
              <CopyButton
                className="text-label-small"
                copyLabel="Role ARN"
                value={primaryRoleARN || ""}
              >
                Copy Role ARN
              </CopyButton>

              <Divider
                className="h-3/4 self-center"
                orientation="vertical"
                size="s"
              />
              <CopyButton
                className="text-label-small"
                copyLabel="External ID"
                value={externalID || ""}
              >
                Copy External ID
              </CopyButton>
            </div>
          </div>
          {isGatewayRolesToggled ? (
            <GatewayRolesForm queryRef={data} viewOnly={userHasGatewayRoles} />
          ) : null}
        </div>
      </div>
    </>
  );
}
